import { isNil, get } from 'lodash';

/**
 * Gets the payload id.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {string} The id for the payload or empty string if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getId = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, '_id', '');
};

/**
 * Get payload name.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {string} The name of the payload or empty string if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getName = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'name', '');
};

/**
 * Get payload registration number.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {string} The registration number of the payload or empty string if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getRegistrationNumber = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'registrationNumber', '');
};

/**
 * Get payload model.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {string} The model of the payload or empty string if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getModel = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'model', '');
};

/**
 * Get payload weight.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {number} The weight of the payload or 0 if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getWeight = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'weight', 0);
};

/**
 * Get payload type.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {string} The type of the payload or empty string if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getPayloadType = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'payloadType', '');
};

/**
 * Get payload sensor parameters.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {object} The sensor parameters of the payload or empty object if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getSensorParameters = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'sensorParameters', {});
};

/**
 * Get payload focal length.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {number} The focal length of the payload or 0 if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getFocalLength = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'sensorParameters.focalLength', 0);
};

/**
 * Get payload sensor width.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {number} The sensor width of the payload or 0 if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getSensorWidth = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'sensorParameters.sensorWidth', 0);
};

/**
 * Get payload sensor height.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {number} The sensor height of the payload or 0 if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getSensorHeight = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'sensorParameters.sensorHeight', 0);
};

/**
 * Get payload image width.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {number} The image width of the payload or 0 if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getImageWidth = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'sensorParameters.imageWidth', 0);
};

/**
 * Get payload image height.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {number} The image height of the payload or 0 if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getImageHeight = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'sensorParameters.imageHeight', 0);
};

/**
 * Get payload gimbal parameters.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {object} The gimbal parameters of the payload or empty object if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getGimbalParameters = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'gimbalParameters', {});
};

/**
 * Get payload roll offset angle.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {number} The roll offset angle of the payload or 0 if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getRollOffsetAngle = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'gimbalParameters.rollOffsetAngle', 0);
};

/**
 * Get payload pitch offset angle.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {number} The pitch offset angle of the payload or 0 if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getPitchOffsetAngle = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'gimbalParameters.pitchOffsetAngle', 0);
};

/**
 * Get payload yaw offset angle.
 *
 * @param {object} payloadObject - The payload object.
 * @returns {number} The yaw offset angle of the payload or 0 if not found.
 * @throws {Error} If payload is null or undefined.
 */
export const getYawOffsetAngle = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error('payload not defined');
  }
  return get(payloadObject, 'gimbalParameters.yawOffsetAngle', 0);
};

/**
 * Checks if the payload is deleted.
 *
 * @param {object} payloadObject - The  payload object.
 * @returns {boolean} True if the  payload is in trash, false otherwise.
 * @throws {Error} If  payload is null or undefined.
 */
export const isDeleted = payloadObject => {
  if (isNil(payloadObject)) {
    throw new Error(' payload not defined');
  }
  return get(payloadObject, 'deleted', '') !== 'no';
};
